import { useRef, useState } from 'react'
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile'
import { useTranslation } from 'react-i18next'

import { verifyHuman } from '../../api/humanCheckApi'
import { turnstileSiteKey } from '../../turnstileConfig'
import styles from './HumanCheckModal.module.css'

interface HumanCheckModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

export default function HumanCheckModal({ isOpen, onClose, onSuccess }: HumanCheckModalProps) {
  const { t } = useTranslation()
  const [error, setError] = useState<string | null>(null)
  const [isVerifying, setIsVerifying] = useState(false)
  const turnstileRef = useRef<TurnstileInstance>(null)

  if (!isOpen) return null

  async function handleVerified(token: string) {
    setError(null)
    setIsVerifying(true)
    try {
      await verifyHuman(token)
      onSuccess()
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('human_check_error_generic')
      setError(msg)
      turnstileRef.current?.reset()
    } finally {
      setIsVerifying(false)
    }
  }

  function handleBackdropClick(e: React.MouseEvent<HTMLDivElement>) {
    if (e.target === e.currentTarget) onClose()
  }

  return (
    <div
      className={styles.backdrop}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="human-check-title"
    >
      <div className={styles.modal}>
        <button
          type="button"
          className={styles.closeBtn}
          onClick={onClose}
          aria-label={t('close')}
        >
          ×
        </button>

        <h2 id="human-check-title" className={styles.title}>
          {t('human_check_title')}
        </h2>
        <p className={styles.description}>{t('human_check_description')}</p>

        {isVerifying && (
          <p className={styles.verifying}>{t('human_check_verifying')}</p>
        )}

        {error && <p className={styles.errorMsg}>{error}</p>}

        {turnstileSiteKey && (
          <Turnstile
            ref={turnstileRef}
            siteKey={turnstileSiteKey}
            onSuccess={handleVerified}
            options={{ theme: 'auto' }}
          />
        )}

        {!turnstileSiteKey && (
          <p className={styles.errorMsg}>{t('human_check_not_configured')}</p>
        )}
      </div>
    </div>
  )
}
